import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

export default function CurtainOverlay({ launchDate, onComplete }) {
  const [timeLeft, setTimeLeft] = useState(launchDate - Date.now());
  const [openCurtain, setOpenCurtain] = useState(false);
  const [ripple, setRipple] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      const remaining = launchDate - Date.now();
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timer);
        setOpenCurtain(true);

        // After curtain opens, show welcome text
        setTimeout(() => {
          setShowWelcome(true);

          // After welcome text, trigger onComplete
          setTimeout(() => {
            onComplete();
          }, 3000);
        }, 500); // wait until curtain fully opens
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [launchDate, onComplete]);

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000) % 60;
    const minutes = Math.floor(ms / (1000 * 60)) % 60;
    const hours = Math.floor(ms / (1000 * 60 * 60)) % 24;
    const days = Math.floor(ms / (1000 * 60 * 60 * 24));
    return { days, hours, minutes, seconds };
  };

  const time = formatTime(timeLeft);

  useEffect(() => {
    const rippleInterval = setInterval(() => {
      setRipple(true);
      setTimeout(() => setRipple(false), 1000);
    }, 5000);
    return () => clearInterval(rippleInterval);
  }, []);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-white"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 1 }}
      >
        {/* Curtains */}
        <motion.div
          className="absolute top-0 left-0 h-full w-1/2 z-20 origin-right"
          style={{
            backgroundImage: `url(/assets/images/curtainleft.svg)`,
            backgroundSize: "cover",
            backgroundPosition: "right center",
          }}
          initial={{ x: 0 }}
          animate={{
            x: openCurtain ? "-100%" : 0,
            scaleX: ripple ? [1, 1.02, 1] : 1,
          }}
          transition={{
            x: { duration: 2.5, ease: [0.2, 1, 0.3, 1] },
            scaleX: { duration: 1.5, ease: "easeInOut" },
          }}
        />
        <motion.div
          className="absolute top-0 right-0 h-full w-1/2 z-20 origin-left"
          style={{
            backgroundImage: `url(/assets/images/curtainright.svg)`,
            backgroundSize: "cover",
            backgroundPosition: "left center",
          }}
          initial={{ x: 0 }}
          animate={{
            x: openCurtain ? "100%" : 0,
            scaleX: ripple ? [1, 1.02, 1] : 1,
          }}
          transition={{
            x: { duration: 2.5, ease: [0.2, 1, 0.3, 1] },
            scaleX: { duration: 1.5, ease: "easeInOut" },
          }}
        />

        {/* Countdown */}
        {!openCurtain && (
          <motion.div
            className="relative z-40 text-center p-8 rounded-3xl backdrop-blur-md"
            style={{
              background:
                "radial-gradient(circle at center, rgba(240,248,255,0.85) 0%, rgba(224,238,255,0.95) 100%)",
              border: "1px solid rgba(100,149,237,0.2)",
              boxShadow: "0 0 15px rgba(100,149,237,0.3)",
            }}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-red-600 font-sans tracking-wide">
              LAUNCHING SOON
            </h1>

            <div className="flex justify-center gap-6 mb-8">
              {Object.entries(time).map(([unit, value], i) => (
                <motion.div
                  key={unit}
                  className="text-center"
                  whileHover={{ y: -5 }}
                >
                  <div className="text-5xl md:text-7xl font-bold text-red-700 font-mono">
                    {(i > 0 ? ":" : "") + value.toString().padStart(2, "0")}
                  </div>
                  <div className="text-sm uppercase tracking-widest text-red-500/90 mt-2">
                    {unit}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Welcome Text After Curtain Opens */}
        {showWelcome && (
          <motion.div
            className="absolute z-50 text-center text-red-700 font-bold text-4xl md:text-6xl"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          >
            Welcome to Kavatch Healthcare
          </motion.div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
